import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Input, Modal } from '@bigcommerce/big-design';
import CountriesDropdown from './Fields/CountriesDropdown';
import StatesDropdown from './Fields/StatesDropdown';

const EDIT_BILLING_FIELD_LABELS = {
	first_name: 'First Name',
	last_name: 'Last Name',
	company: 'Company',
	street_1: 'Street 1',
	street_2: 'Street 2',
	city: 'City',
	state: 'State or Province',
	zip: 'Postal Code',
	country: 'Country',
	phone: 'Phone',
	email: 'Email',
};

const EDIT_SHIPPING_FIELD_LABELS = {
	first_name: 'First Name',
	last_name: 'Last Name',
	company: 'Company',
	street_1: 'Street 1',
	street_2: 'Street 2',
	city: 'City',
	state: 'State or Province',
	zip: 'Postal Code',
	country: 'Country',
};

const ADDRESS_REQUIRED_FIELDS = [
	'first_name',
	'last_name',
	'street_1',
	'city',
	'state',
	'zip',
	'country',
];

const COUNTRY_ISO2_LOOKUP = new Map([
	['United States', 'US'],
	['Canada', 'CA'],
]);

function selectAddressFieldRender(
	property,
	value,
	country,
	onAddressDataChange,
) {
	switch (property) {
		case 'country':
			return (
				<CountriesDropdown
					getValue={() => value || ''}
					setValue={(oldValue, newValue) =>
						onAddressDataChange({
							country_iso2: COUNTRY_ISO2_LOOKUP.get(newValue),
							[property]: newValue,
							state: '',
						})
					}
				/>
			);
		case 'state':
			return (
				<StatesDropdown
					getValue={() => value}
					setValue={(oldValue, newValue) =>
						onAddressDataChange({
							[property]: newValue,
						})
					}
					country={country}
				/>
			);
		default:
			return (
				<Input
					value={value}
					onChange={(event) =>
						onAddressDataChange({
							[property]: event.currentTarget.value,
						})
					}
				/>
			);
	}
}

const isRequired = (property) => ADDRESS_REQUIRED_FIELDS.includes(property);

function buildErrorMessage(invalidFields, labels) {
	const fields = invalidFields.map((field, index) => {
		const text = `${labels[field]}, ${
			index === invalidFields.length - 2 ? 'and ' : ''
		}`;
		return text;
	});
	return `${fields.join('')}are required fields.`;
}

function validateField(field, value) {
	return (
		!ADDRESS_REQUIRED_FIELDS.includes(field) ||
		String(value ?? '').length > 0
	);
}

const StyledForm = styled.form`
	margin: 2rem;
	display: grid;
	grid-template-columns: 1fr 3fr;
	row-gap: 1rem;
`;

const StyledError = styled.div`
	color: #c82424;
	border: 1px solid #c82424;
	padding: 5px;
	background: #ffe5e5;
	border-radius: 3px;
`;

const RequiredIcon = styled.span`
	color: #c82424;
	padding-right: 2px;
`;

export function CustomerInfoEditForm({ address, title, onCancel, onComplete }) {
	const [editAddress, setEditAddress] = useState(() => ({
		company: '',
		...(address ?? {}),
	}));
	const [errorMessage, setErrorMessage] = useState('');

	const labels =
		title === 'Billing'
			? EDIT_BILLING_FIELD_LABELS
			: EDIT_SHIPPING_FIELD_LABELS;

	const handleFormSubmit = useCallback(() => {
		const invalidFields = ADDRESS_REQUIRED_FIELDS.filter(
			(field) => !validateField(field, editAddress[field]),
		);

		if (invalidFields.length > 0) {
			setErrorMessage(buildErrorMessage(invalidFields, labels));
			return;
		}
		onComplete(editAddress, title);
	}, [editAddress, labels, title, onComplete]);

	return (
		<Modal
			header={`Edit ${title} Address`}
			isOpen={true}
			closeOnClickOutside={true}
			onClose={onCancel}
			actions={[
				{ text: 'Cancel', variant: 'secondary', onClick: onCancel },
				{
					text: 'Apply',
					variant: 'primary',
					onClick: handleFormSubmit,
				},
			]}
		>
			{errorMessage && (
				<StyledError className="error">{errorMessage}</StyledError>
			)}
			<StyledForm>
				{Object.keys(labels).map((property) => (
					<>
						<p className="modal-label">
							{isRequired(property) && (
								<RequiredIcon>*</RequiredIcon>
							)}
							{labels[property]}
						</p>
						{selectAddressFieldRender(
							property,
							editAddress[property] ?? '',
							editAddress.country,
							(updatedData) =>
								setEditAddress({
									...editAddress,
									...updatedData,
								}),
						)}
					</>
				))}
			</StyledForm>
		</Modal>
	);
}
